import React, { useEffect, useMemo, useState } from 'react';
import IllustratedCharacter from '../../../components/character/IllustratedCharacter';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, collectionGroup, query, where, getDocs, onSnapshot, doc, getDoc, orderBy } from 'firebase/firestore';
import type { DocumentSnapshot } from 'firebase/firestore';
import { db, getClassPointRankingMode, getTotalEarnedPointsByStudent, resolveStudentClassId } from '../../../utils/firebase';
import { School, Student, isStudent } from '../../../utils/firebase';
import { 
  subscribeToUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  getNotificationTypeColor,
  getNotificationTypeIcon,
  getNotificationBackgroundColor
} from '../../../utils/notifications';
import { getCurrentTimetableStatus } from '../../../utils/timetablePeriod';
import { Notification } from '../../../types/user';
import { Schedule } from '../../../types/schedule';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import TodayMealCard from '../../../components/ui/TodayMealCard';
import { useActiveBirthdayRollingPaperId } from '../../../hooks/useActiveBirthdayRollingPaperId';
import type { CharacterLayerSlot, CharacterLoadout, CharacterStats } from '../../../types/character';
import {
  CHARACTER_LAYER_RENDER_ORDER,
  CHARACTER_STAT_LABELS,
  CHARACTER_STAT_KEYS,
  DEFAULT_CHARACTER_STATS,
  DEFAULT_CHARACTER_LEVEL,
} from '../../../types/character';
import { normalizeCharacterStats, normalizeCharacterLevel } from '../../../utils/characterStats';
import { FEATURE_CHARACTER_COSMETIC } from '../../../config/featureFlags';
import {
  DEFAULT_CHARACTER_PRESET,
  getCharacterPreset,
  getEquippedCharacterImageUrl,
  type CharacterPresetId,
} from '../../../utils/characterPresets';
import {
  fetchNeisTimetable,
  hasTimetableSubjects,
  getAcademicYearAndSemester,
  getWeekdayRange,
  createEmptyTimetable,
  type Timetable,
} from '../../../utils/neis';
import { fetchComciganTimetable } from '../../../utils/comcigan';

interface StudentRole {
  id: string;
  name: string;
  salary: number;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
}

interface EventNotification {
  id: string;
  content: string;
  createdAt: Date;
}

interface StoreCosmeticItem {
  imageUrl?: string;
  characterSlot?: CharacterLayerSlot;
  characterOutfitId?: string;
}

/** 3–8월은 1학기, 9–2월은 2학기 */
const schoolTermLabel = (date = new Date()) => {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  if (month >= 3 && month <= 8) return `${year}년 1학기`;
  if (month >= 9) return `${year}년 2학기`;
  return `${year - 1}년 2학기`;
};

const StudentCharacterPortrait: React.FC<{
  src: string;
  overlays?: string[];
  width: number;
  height: number;
}> = ({ src, overlays = [], width, height }) => (
  <div
    style={{
      position: 'relative',
      width,
      height,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0,
    }}
  >
    <IllustratedCharacter
      src={src}
      alt="내 캐릭터"
      style={{
        width,
        height,
        objectFit: 'contain',
        objectPosition: 'center bottom',
      }}
    />
    {overlays.map((imageUrl) => (
      <img
        key={imageUrl}
        src={imageUrl}
        alt=""
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width,
          height,
          objectFit: 'contain',
          objectPosition: 'center bottom',
          pointerEvents: 'none',
        }}
      />
    ))}
  </div>
);


const ClassStudentHome: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const activeBirthdayRollingPaperId = useActiveBirthdayRollingPaperId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<School | null>(null);
  const [studentData, setStudentData] = useState<Student | null>(null);
  const [myRank, setMyRank] = useState<number>(0);
  const [studentCount, setStudentCount] = useState<number>(0);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [myRole, setMyRole] = useState<StudentRole | null>(null);
  const [monthlySalary, setMonthlySalary] = useState<number>(0);
  const [eventNotifications, setEventNotifications] = useState<EventNotification[]>([]);
  const [classInfo, setClassInfo] = useState<{ grade?: number; class?: number; teacherName?: string } | null>(null);
  const [classPoints, setClassPoints] = useState<number>(0);
  const [characterLoadout, setCharacterLoadout] = useState<CharacterLoadout>({});
  const [characterInventory, setCharacterInventory] = useState<string[]>([]);
  const [characterPreset, setCharacterPreset] = useState<CharacterPresetId>(DEFAULT_CHARACTER_PRESET);
  const [characterStats, setCharacterStats] = useState<CharacterStats>({ ...DEFAULT_CHARACTER_STATS });
  const [characterLevel, setCharacterLevel] = useState<number>(DEFAULT_CHARACTER_LEVEL);
  const [storeCosmeticsById, setStoreCosmeticsById] = useState<Record<string, StoreCosmeticItem>>({});
  
  // 학급 사진 관련 상태
  const [classPhotos, setClassPhotos] = useState<string[]>(['/images/class/ClassSampleImage.png']);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // 알림 관련 상태
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
  
  // 모바일 감지 상태
  const [isMobile, setIsMobile] = useState(false);
  
  // 학급에 저장된 시간표 (교사가 저장한 값)
  const [classTimetable, setClassTimetable] = useState<Timetable>(createEmptyTimetable());
  // 나이스(학교) 시간표
  const [neisTimetable, setNeisTimetable] = useState<Timetable | null>(null);
  const [neisTimetableLoading, setNeisTimetableLoading] = useState(false);
  const [comciganTimetable, setComciganTimetable] = useState<Timetable | null>(null);
  const [comciganTimetableLoading, setComciganTimetableLoading] = useState(false);

  // 일정 날짜 비교 헬퍼 함수
  const isScheduleOnDate = (schedule: Schedule, date: number, year?: number, month?: number): boolean => {
    const now = new Date();
    const checkYear = year ?? now.getFullYear();
    const checkMonth = month ?? now.getMonth();
    
    const normalizeDate = (d: Date) => {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };
    
    if (schedule.dateOption === 'allDay' && schedule.date) {
      const scheduleDate = schedule.date.toDate();
      return scheduleDate.getDate() === date && 
             scheduleDate.getMonth() === checkMonth && 
             scheduleDate.getFullYear() === checkYear;
    }
    
    if (schedule.dateOption === 'custom' && schedule.startDate && schedule.endDate) {
      const startDate = normalizeDate(schedule.startDate.toDate());
      const endDate = normalizeDate(schedule.endDate.toDate());
      const checkDate = new Date(checkYear, checkMonth, date);
      const normalizedCheckDate = normalizeDate(checkDate);
      return normalizedCheckDate >= startDate && normalizedCheckDate <= endDate;
    }
    
    return false;
  };

  // 달력 생성 함수
  const generateCalendar = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    const calendar = [];
    
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      calendar.push({
        date: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
      calendar.push({
        date: day,
        isCurrentMonth: true,
        isToday
      });
    }
    
    const remainingCells = 42 - calendar.length;
    for (let day = 1; day <= remainingCells; day++) {
      calendar.push({
        date: day,
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return calendar;
  };
  
  // 모바일 감지 useEffect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    if (!currentUser || !isStudent(currentUser)) return;

    const student = currentUser as Student;
    const unsubscribeFunctions: (() => void)[] = [];

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 학교 정보 가져오기
        try {
          const schoolRef = doc(db, 'schools', student.schoolId);
          const schoolDoc = await getDoc(schoolRef);
          
          if (schoolDoc.exists()) {
            setSchoolInfo(schoolDoc.data() as School);
          }
        } catch (schoolError) {
          console.error('학교 정보 가져오기 실패:', schoolError);
        }

        // 학생 정보 가져오기 — 학급 ID는 문서 경로 우선(필드 오류 보정), 학급 문서는 실시간 구독(교사 사진 반영)
        let effectiveClassId = '';
        if (student.schoolId && student.uid) {
          try {
            let studentDoc: DocumentSnapshot | null = null;
            if (student.classId) {
              studentDoc = await getDoc(
                doc(db, `schools/${student.schoolId}/classes/${student.classId}/students/${student.uid}`)
              );
            }
            if (!studentDoc?.exists()) {
              const uidQ = query(collectionGroup(db, 'students'), where('uid', '==', student.uid));
              const uidSnap = await getDocs(uidQ);
              const match = uidSnap.docs.find((d) =>
                d.ref.path.startsWith(`schools/${student.schoolId}/classes/`)
              );
              if (match) {
                studentDoc = match;
              }
            }

            const merged: Record<string, unknown> = {
              ...student,
              ...(studentDoc?.exists() ? studentDoc.data() : {})
            };
            effectiveClassId = studentDoc?.exists()
              ? resolveStudentClassId(studentDoc, merged as Record<string, any>)
              : student.classId || '';

            if (studentDoc?.exists()) {
              const data = studentDoc.data();
              setStudentData({
                ...student,
                ...data,
                points: data.points || 0
              } as Student);
            }

            const applyClassDocument = async (classData: Record<string, unknown>, exists: boolean) => {
              if (!exists) {
                setClassPhotos(['/images/class/ClassSampleImage.png']);
                let fallbackTeacher: string | undefined;
                if (student.teacherId) {
                  try {
                    const tdoc = await getDoc(
                      doc(db, `schools/${student.schoolId}/teachers/${student.teacherId}`)
                    );
                    if (tdoc.exists()) {
                      const td = tdoc.data();
                      fallbackTeacher = td.name || td.teacherName || td.displayName;
                    }
                  } catch {
                    /* ignore */
                  }
                }
                setClassInfo({
                  grade: student.grade != null ? Number(student.grade) : undefined,
                  class: student.class != null ? Number(student.class) : undefined,
                  teacherName: fallbackTeacher
                });
                return;
              }
              let teacherName: string | undefined =
                typeof classData.teacherName === 'string' ? classData.teacherName : undefined;
              if (!teacherName) {
                const tid =
                  classData.teacherId ||
                  classData.teacherUid ||
                  student.teacherId;
                if (tid) {
                  try {
                    const tdoc = await getDoc(doc(db, `schools/${student.schoolId}/teachers/${String(tid)}`));
                    if (tdoc.exists()) {
                      const td = tdoc.data();
                      teacherName = td.name || td.teacherName || td.displayName;
                    }
                  } catch {
                    /* ignore */
                  }
                }
              }
              if (!teacherName) {
                try {
                  const tq = query(
                    collection(db, `schools/${student.schoolId}/teachers`),
                    where('classId', '==', effectiveClassId)
                  );
                  const ts = await getDocs(tq);
                  if (!ts.empty) {
                    const td = ts.docs[0].data();
                    teacherName = td.name || td.teacherName || td.displayName;
                  }
                } catch {
                  /* ignore */
                }
              }
              const gradeVal =
                classData.grade != null
                  ? Number(classData.grade)
                  : student.grade != null
                    ? Number(student.grade)
                    : undefined;
              const classVal =
                classData.class != null
                  ? Number(classData.class)
                  : classData.classNumber != null
                    ? Number(classData.classNumber)
                    : student.class != null
                      ? Number(student.class)
                      : undefined;
              setClassInfo({
                grade: gradeVal,
                class: classVal,
                teacherName
              });
              setClassPoints(typeof classData.classPoints === 'number' ? classData.classPoints : 0);
              const rawPhotos = classData.photos;
              if (rawPhotos && Array.isArray(rawPhotos) && rawPhotos.length > 0) {
                const urls = rawPhotos.filter((p) => typeof p === 'string' && p.length > 0) as string[];
                setClassPhotos(urls.length > 0 ? urls : ['/images/class/ClassSampleImage.png']);
              } else {
                setClassPhotos(['/images/class/ClassSampleImage.png']);
              }
            };

            if (effectiveClassId) {
              try {
                const classRef = doc(db, `schools/${student.schoolId}/classes/${effectiveClassId}`);
                const unsubClass = onSnapshot(
                  classRef,
                  (snap) => {
                    void applyClassDocument(snap.data() || {}, snap.exists());
                  },
                  (e) => console.error('학급 문서 구독 오류:', e)
                );
                unsubscribeFunctions.push(unsubClass);
              } catch (classError) {
                console.error('학급 정보 구독 실패:', classError);
              }
            }

            // 시간표·학급 통계·역할 (보정된 학급 ID 사용)
            if (effectiveClassId) {
            const timetableRef = doc(db, `schools/${student.schoolId}/classes/${effectiveClassId}/timetable/current`);
            const unsubscribeTimetable = onSnapshot(
              timetableRef,
              (timetableDoc) => {
                if (timetableDoc.exists()) {
                  const saved = timetableDoc.data();
                  setClassTimetable({
                    semester: saved.semester || '1학기',
                    year: saved.year || new Date().getFullYear(),
                    entries: saved.entries || []
                  });
                } else {
                  setClassTimetable(createEmptyTimetable());
                }
              },
              (timetableError) => {
                console.error('시간표 정보 가져오기 실패:', timetableError);
                setClassTimetable(createEmptyTimetable());
              }
            );
            unsubscribeFunctions.push(unsubscribeTimetable);

            // 학생 수 및 내 순위 계산 (학급 포인트 등수 기준 설정 반영)
            const studentsRef = collection(db, `schools/${student.schoolId}/classes/${effectiveClassId}/students`);
            const studentsSnapshot = await getDocs(studentsRef);
            setStudentCount(studentsSnapshot.size);

            const rankingMode = await getClassPointRankingMode(student.schoolId, effectiveClassId);
            let pointsByStudent: Record<string, number> = {};
            if (rankingMode === 'totalEarned') {
              pointsByStudent = await getTotalEarnedPointsByStudent(student.schoolId, effectiveClassId);
            }
            const allStudents = studentsSnapshot.docs.map(d => {
              const data = d.data();
              const balance = data.points || 0;
              const rankPoints = rankingMode === 'totalEarned' ? (pointsByStudent[d.id] || 0) : balance;
              return { id: d.id, points: rankPoints };
            });
            const sortedStudents = allStudents.sort((a, b) => b.points - a.points);
            const myIndex = sortedStudents.findIndex(s => s.id === student.uid);
            setMyRank(myIndex >= 0 ? myIndex + 1 : 0);

            // 내 역할 정보 가져오기
            if (studentDoc?.exists()) {
              const data = studentDoc.data();
              if (data?.roleId) {
                try {
                  const roleRef = doc(db, `schools/${student.schoolId}/classes/${effectiveClassId}/roles`, data.roleId);
                  const roleDoc = await getDoc(roleRef);
                  
                  if (roleDoc.exists()) {
                    const roleData = roleDoc.data();
                    setMyRole({
                      id: roleDoc.id,
                      name: roleData.name || '',
                      salary: roleData.salary || 0,
                      salaryType: roleData.salaryType || 'point',
                      salaryPeriod: roleData.salaryPeriod || 'monthly'
                    });

                    // 월급 계산
                    if (roleData.salaryPeriod === 'monthly') {
                      setMonthlySalary(roleData.salary || 0);
                    } else if (roleData.salaryPeriod === 'weekly') {
                      setMonthlySalary((roleData.salary || 0) * 4);
                    } else if (roleData.salaryPeriod === 'daily') {
                      setMonthlySalary((roleData.salary || 0) * 30);
                    }
                  }
                } catch (roleError) {
                  console.error('역할 정보 가져오기 실패:', roleError);
                }
              }
            }
            }
          } catch (studentsError) {
            console.error('학생 정보 가져오기 실패:', studentsError);
          }
        }

        // 일정 데이터 조회
        if (effectiveClassId) {
          const schedulesRef = collection(
            db,
            `schools/${student.schoolId}/classes/${effectiveClassId}/schedules`
          );
          
          const q = query(
            schedulesRef,
            where('type', '==', 'class'),
            where('isActive', '==', true),
            orderBy('createdAt', 'desc')
          );
          
          const unsubscribeSchedules = onSnapshot(
            q,
            (snapshot) => {
              const fetchedSchedules = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              })) as Schedule[];
              
              setSchedules(fetchedSchedules);
            },
            (error) => {
              console.error('일정 데이터 조회 오류:', error);
            }
          );
          unsubscribeFunctions.push(unsubscribeSchedules);
        }
      } catch (err) {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // cleanup 함수: 모든 구독 해제
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [currentUser]);

  // 나이스 학교 시간표 불러오기 (학교 코드가 있으면 이번 주 실제 시간표 우선)
  useEffect(() => {
    const officeCode = schoolInfo?.neisOfficeCode?.trim();
    const schoolCode = schoolInfo?.neisSchoolCode?.trim();
    const schoolType = schoolInfo?.type;
    const grade = classInfo?.grade ?? (studentData?.grade != null ? Number(studentData.grade) : undefined);
    const classNum = classInfo?.class ?? (studentData?.class != null ? Number(studentData.class) : undefined);

    if (!officeCode || !schoolCode || !schoolType || !grade || !classNum) {
      setNeisTimetable(null);
      setNeisTimetableLoading(false);
      return;
    }

    let cancelled = false;
    const academic = getAcademicYearAndSemester();
    const week = getWeekdayRange();
    const cacheKey = `maru.neisTimetable:${officeCode}:${schoolCode}:${grade}:${classNum}:${week.from}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached) as Timetable;
        if (hasTimetableSubjects(parsed.entries)) {
          setNeisTimetable(parsed);
          setNeisTimetableLoading(false);
          return;
        }
      }
    } catch {
      /* ignore */
    }

    const loadNeisTimetable = async () => {
      setNeisTimetableLoading(true);
      try {
        const result = await fetchNeisTimetable({
          officeCode,
          schoolCode,
          year: academic.year,
          semester: academic.semester,
          grade,
          classNum,
          schoolType,
        });
        if (!cancelled) {
          setNeisTimetable(result);
          if (hasTimetableSubjects(result.entries)) {
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify(result));
            } catch {
              /* ignore */
            }
          }
        }
      } catch (err) {
        console.error('나이스 시간표 불러오기 실패:', err);
        if (!cancelled) {
          setNeisTimetable(null);
        }
      } finally {
        if (!cancelled) {
          setNeisTimetableLoading(false);
        }
      }
    };

    void loadNeisTimetable();
    return () => {
      cancelled = true;
    };
  }, [
    schoolInfo?.neisOfficeCode,
    schoolInfo?.neisSchoolCode,
    schoolInfo?.type,
    classInfo?.grade,
    classInfo?.class,
    studentData?.grade,
    studentData?.class,
  ]);

  // 컴시간알리미 시간표 (학교 코드가 있으면 이번 주 변경 시간표 우선)
  useEffect(() => {
    const schoolCode = Number(schoolInfo?.comciganSchoolCode) || 0;
    const grade = classInfo?.grade ?? (studentData?.grade != null ? Number(studentData.grade) : undefined);
    const classNum = classInfo?.class ?? (studentData?.class != null ? Number(studentData.class) : undefined);

    if (!schoolCode || !grade || !classNum) {
      setComciganTimetable(null);
      setComciganTimetableLoading(false);
      return;
    }

    let cancelled = false;
    const week = getWeekdayRange();
    const cacheKey = `maru.comciganTimetable:${schoolCode}:${grade}:${classNum}:${week.from}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached) as Timetable;
        if (hasTimetableSubjects(parsed.entries)) {
          setComciganTimetable(parsed);
          setComciganTimetableLoading(false);
          return;
        }
      }
    } catch {
      /* ignore */
    }

    const loadComciganTimetable = async () => {
      setComciganTimetableLoading(true);
      try {
        const result = await fetchComciganTimetable({
          schoolCode,
          grade,
          classNum,
        });
        if (!cancelled) {
          setComciganTimetable(result);
          if (hasTimetableSubjects(result.entries)) {
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify(result));
            } catch {
              /* ignore */
            }
          }
        }
      } catch (err) {
        console.error('컴시간 시간표 불러오기 실패:', err);
        if (!cancelled) {
          setComciganTimetable(null);
        }
      } finally {
        if (!cancelled) {
          setComciganTimetableLoading(false);
        }
      }
    };

    void loadComciganTimetable();
    return () => {
      cancelled = true;
    };
  }, [
    schoolInfo?.comciganSchoolCode,
    classInfo?.grade,
    classInfo?.class,
    studentData?.grade,
    studentData?.class,
  ]);

  // 알림 구독
  useEffect(() => {
    if (!currentUser?.uid) return;

    const unsubscribe = subscribeToUserNotifications(
      currentUser.uid,
      (notifications) => {
        setNotifications(notifications);
        setUnreadNotificationCount(notifications.filter(n => !n.isRead).length);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // 이벤트 알림 가져오기
  useEffect(() => {
    if (!currentUser || !isStudent(currentUser) || !currentUser.classId) return;

    const student = currentUser as Student;
    const noticesRef = collection(
      db,
      `schools/${student.schoolId}/classes/${student.classId}/notices`
    );
    
    const q = query(noticesRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notices = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          content: data.content || '',
          createdAt: data.createdAt?.toDate() || new Date()
        };
      });
      setEventNotifications(notices);
    });

    return () => unsubscribe();
  }, [currentUser]);


  // 알림 관련 핸들러
  const handleNotificationClick = () => {
    setOpenNotificationDialog(true);
  };

  const handleNotificationClose = () => {
    setOpenNotificationDialog(false);
  };

  const handleMarkNotificationAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(currentUser?.uid || '', notificationId);
    } catch (error) {
      console.error('알림 읽음 처리 중 오류:', error);
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    try {
      await markAllNotificationsAsRead(currentUser?.uid || '');
    } catch (error) {
      console.error('모든 알림 읽음 처리 중 오류:', error);
    }
  };

  // 사진 네비게이션
  const goToPhoto = (index: number) => {
    setCurrentPhotoIndex(index);
  };

  const timetableData = useMemo<Timetable>(() => {
    if (comciganTimetable && hasTimetableSubjects(comciganTimetable.entries)) {
      return comciganTimetable;
    }
    if (neisTimetable && hasTimetableSubjects(neisTimetable.entries)) {
      return neisTimetable;
    }
    if (hasTimetableSubjects(classTimetable.entries)) {
      return classTimetable;
    }
    return classTimetable.entries.length > 0 ? classTimetable : createEmptyTimetable();
  }, [comciganTimetable, neisTimetable, classTimetable]);

  const timetableSource: 'comcigan' | 'neis' | 'class' | 'empty' =
    comciganTimetable && hasTimetableSubjects(comciganTimetable.entries)
      ? 'comcigan'
      : neisTimetable && hasTimetableSubjects(neisTimetable.entries)
        ? 'neis'
        : hasTimetableSubjects(classTimetable.entries)
          ? 'class'
          : 'empty';

  // 교시 전환 감지를 위해 매분 정각에 갱신
  const [timetableNow, setTimetableNow] = useState(() => new Date());
  useEffect(() => {
    let intervalId: number | undefined;
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000) + 50;
    const timeoutId = window.setTimeout(() => {
      setTimetableNow(new Date());
      intervalId = window.setInterval(() => setTimetableNow(new Date()), 60_000);
    }, msUntilNextMinute);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, []);

  const currentTimetableStatus = useMemo(
    () => getCurrentTimetableStatus(timetableData.entries, timetableNow),
    [timetableData.entries, timetableNow]
  );
  const currentPeriod =
    currentTimetableStatus?.kind === 'period' ? currentTimetableStatus : null;
  const characterImage = useMemo(
    () =>
      getEquippedCharacterImageUrl(
        characterPreset,
        characterLoadout.base,
        characterInventory,
        storeCosmeticsById
      ),
    [characterInventory, characterLoadout.base, characterPreset, storeCosmeticsById]
  );

  const equippedLayerImages = useMemo(
    () =>
      CHARACTER_LAYER_RENDER_ORDER
        .filter((slot) => slot !== 'base')
        .map((slot) => characterLoadout[slot])
        .filter((itemId): itemId is string =>
          Boolean(
            itemId &&
            characterInventory.includes(itemId) &&
            storeCosmeticsById[itemId]?.imageUrl &&
            !storeCosmeticsById[itemId]?.characterOutfitId
          )
        )
        .map((itemId) => storeCosmeticsById[itemId].imageUrl as string),
    [characterInventory, characterLoadout, storeCosmeticsById]
  );

  useEffect(() => {
    if (!currentUser || !isStudent(currentUser) || !currentUser.schoolId || !currentUser.classId) return;

    const studentRef = doc(
      db,
      `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students/${currentUser.uid}`
    );
    const unsubStudent = onSnapshot(studentRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setCharacterStats(normalizeCharacterStats(data.characterStats));
      setCharacterLevel(normalizeCharacterLevel(data.characterLevel));
      setCharacterPreset(getCharacterPreset(data.characterPreset as string | undefined).id);
      setCharacterLoadout((data.characterLoadout as CharacterLoadout) || {});
      setCharacterInventory((data.characterInventory as string[]) || []);
    });

    const storeRef = collection(
      db,
      `schools/${currentUser.schoolId}/classes/${currentUser.classId}/storeItems`
    );
    const unsubStore = onSnapshot(storeRef, (snap) => {
      const next: Record<string, StoreCosmeticItem> = {};
      snap.docs.forEach((d) => {
        const item = d.data();
        if (item.itemCategory === 'cosmetic') {
          next[d.id] = {
            imageUrl: item.imageUrl,
            characterSlot: item.characterSlot,
            characterOutfitId: item.characterOutfitId
          };
        }
      });
      setStoreCosmeticsById(next);
    });

    return () => {
      unsubStudent();
      unsubStore();
    };
  }, [currentUser]);

  if (!currentUser || !isStudent(currentUser)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        {loading ? (
          <div>로딩 중...</div>
        ) : (
          <div style={{ 
            color: 'red', 
            padding: '20px', 
            border: '1px solid red', 
            borderRadius: '4px' 
          }}>
            학생 권한이 필요합니다.
          </div>
        )}
      </div>
    );
  }

  const student = currentUser as Student;

  const eventNotificationsBlock = (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        minHeight: 0,
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <div
          style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '18px',
            fontFamily: 'Pretendard',
            fontWeight: '600',
            lineHeight: '27px',
            margin: 0,
          }}
        >
          이벤트 알림
        </div>
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'Pretendard',
            fontWeight: '400',
            lineHeight: '21px',
            margin: 0,
          }}
        >
          새로운 알림 {eventNotifications.length}개
        </div>
      </div>
      <div
        style={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '28px',
            padding: '4px 8px',
            background: 'var(--Color-background-sub, #F8F8F8)',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '16px',
            color: 'var(--Color-text-assistive-strong, #4C4C4C)',
            fontSize: '13px',
            fontFamily: 'Pretendard',
            fontWeight: '500',
            lineHeight: '19.50px',
          }}
        >
          <div style={{ width: '17px', flexShrink: 0 }}>No</div>
          <div style={{ flex: 1, minWidth: 0 }}>내용</div>
          <div style={{ flexShrink: 0, fontSize: '12px' }}>등록 날짜</div>
        </div>
        <div
          style={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {eventNotifications.length > 0 ? (
            eventNotifications.slice(0, 5).map((notice, index) => (
              <div
                key={notice.id}
                style={{
                  width: '100%',
                  minHeight: '28px',
                  padding: '8px',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '16px',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                }}
              >
                <div
                  style={{
                    width: '17px',
                    flexShrink: 0,
                    color:
                      index < 3
                        ? 'var(--Color-text-normal, #161616)'
                        : 'var(--Color-text-assistive, #989898)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: index < 3 ? '500' : '400',
                    lineHeight: '19.50px',
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    color:
                      index < 3
                        ? 'var(--Color-text-normal, #161616)'
                        : 'var(--Color-text-assistive, #989898)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: index < 3 ? '500' : '400',
                    lineHeight: '19.50px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {index < 3 && (
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginRight: '2px',
                      }}
                    >
                      <div
                        style={{
                          width: '12.80px',
                          height: '12.80px',
                          background: 'var(--Color-text-brand-color, #FF6F0F)',
                          borderRadius: '50%',
                        }}
                      />
                    </div>
                  )}
                  <span>{notice.content}</span>
                </div>
                <div
                  style={{
                    flexShrink: 0,
                    maxWidth: '88px',
                    color:
                      index < 3
                        ? 'var(--Color-text-normal, #161616)'
                        : 'var(--Color-text-assistive, #989898)',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: index < 3 ? '500' : '400',
                    lineHeight: '19.50px',
                  }}
                >
                  {notice.createdAt.toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '')}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                width: '100%',
                minHeight: '120px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
              }}
            >
              알림이 없습니다
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      width: '100%',
      minHeight: 0,
      padding: isMobile ? '16px' : '0',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible',
    }}>
      {/* 학교 로고 및 학교 이름 표시*/}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gap-level-3, 8px)',
        alignSelf: 'stretch',
        paddingLeft: isMobile ? '0' : '0',
        paddingRight: isMobile ? '0' : '0',
        flexShrink: 0,
      }}>
        {schoolInfo?.logo ? (
          <img 
            src={schoolInfo.logo} 
            alt={`${schoolInfo.name} 로고`}
            style={{
              width: '24px',
              height: '24px',
              objectFit: 'contain',
              borderRadius: '4px'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
              if (nextElement) {
                nextElement.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div 
          style={{
            width: '24px',
            height: '24px',
            backgroundColor: '#1976d2',
            borderRadius: '4px',
            display: schoolInfo?.logo ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px',
            fontWeight: 'bold'
          }}
        >
          🏫
        </div>
        <div style={{ 
          margin: 0,
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: 'var(--Font-Font-size-Body-2, 16px)',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '24px'
        }}>
          {schoolInfo?.name || '학교 정보를 불러오는 중...'}
        </div>
      </div>

      {/* 인사말 헤더*/}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gap-level-3, 8px)',
        alignSelf: 'stretch',
        padding: isMobile ? '8px 0px 16px 0px' : '8px 0px 32px 0px',
        flexShrink: 0,
      }}>
        <h1 style={{ 
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: isMobile ? '24px' : 'var(--Font-Font-size-Heading-2, 32px)',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
          letterSpacing: isMobile ? '-0.48px' : '-0.64px',
          margin: 0
        }}>
          만나서 반갑습니다, {student.name}님
        </h1>
      </div>

      {FEATURE_CHARACTER_COSMETIC && isMobile && (
        <Card
          width="100%"
          padding="16px 20px"
          alignItems="flex-start"
          gap={8}
          borderRadius="12px"
          background="var(--Color-background-normal)"
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '12px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '27px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <span
                style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, pretendard)',
                  fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '150%',
                  letterSpacing: '-0.18px',
                  margin: 0,
                }}
              >
                내 캐릭터
              </span>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'Pretendard',
                  color: 'var(--Color-fill-brand-color, #FF6F0F)',
                  lineHeight: '21px',
                  flexShrink: 0,
                }}
              >
                Lv.{characterLevel}
              </span>
            </div>
            <div
              style={{
                width: '100%',
                padding: '16px',
                background: 'var(--Color-background-sub, #F8F8F8)',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                }}
              >
                  <StudentCharacterPortrait
                    src={characterImage}
                    overlays={equippedLayerImages}
                    width={96}
                    height={168}
                  />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px 12px',
                  fontSize: '12px',
                  fontFamily: 'Pretendard',
                  color: 'var(--Color-text-normal, #161616)',
                  fontWeight: 500,
                }}
                aria-label="캐릭터 능력치"
              >
                {CHARACTER_STAT_KEYS.map((key) => (
                  <span key={key}>
                    {CHARACTER_STAT_LABELS[key]} {characterStats[key]}
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', width: '100%' }}>
                <button
                  type="button"
                  onClick={() => navigate('/class/student/ClassItemShop')}
                  style={{
                    height: '36px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--Color-line-normal, #F0F0F0)',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  코스메틱 상점
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/class/student/CharacterWardrobe')}
                  style={{
                    height: '36px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'var(--Color-fill-normal, #293044)',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  옷장 열기
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 중요 알림 창*/}
      <div style={{ 
        width: '100%',
        height: isMobile ? 'auto' : '40px',
        minHeight: isMobile ? 'auto' : '40px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '12px' : '18px',
        paddingLeft: isMobile ? '0' : '0',
        paddingRight: isMobile ? '0' : '0',
        flexShrink: 0,
      }}>
        <div style={{ 
          display: 'flex',
          width: '100%',
          height: isMobile ? 'auto' : '100%',
          minHeight: isMobile ? '40px' : '100%',
          alignItems: 'center',
          gap: '8px',
          borderRadius: '8px',
          border: '1.2px solid #293044',
          background: '#293044',
          padding: isMobile ? '12px' : '0',
        }}>
          <div style={{
            display: 'flex',
            marginLeft: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            width: '40px',
            height: '22px',
            flexShrink: 0,
            borderRadius: '8px',
            border: '1px solid #F0F0F0',
            background: '#FFF'
          }}>
            <span style={{
              color: 'var(--Color-status-negative, #FF3838)',
              fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
              fontSize: 'var(--Font-Font-size-Label-2, 12px)',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: '150%'
            }}>
              중요
            </span>
          </div>
          <p style={{
            color: 'var(--Color-text-inverse, #FFF)',
            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
            fontSize: 'var(--Font-Font-size-Body-4, 13px)',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '150%',
            margin: 0,
            flex: 1,
          }}>
            {eventNotifications.length > 0 
              ? eventNotifications[0].content 
              : '현재 노출 중인 알림이 없습니다.'}
          </p>
        </div>
      </div>

      {activeBirthdayRollingPaperId ? (
        <div
          style={{
            width: '100%',
            marginBottom: isMobile ? 12 : 18,
          }}
        >
          <Card
            width="100%"
            padding={isMobile ? '16px 18px' : '18px 22px'}
            borderRadius="12px"
            background="var(--Color-background-normal, #fff)"
            style={{
              border: '1px solid var(--Color-line-normal, #F0F0F0)',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: isMobile ? 16 : 17,
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    marginBottom: 6,
                    fontFamily: 'var(--font-family-primary, Pretendard)',
                  }}
                >
                  생일 축하 롤링페이퍼
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: 'var(--Color-text-normal, #161616)',
                    fontFamily: 'var(--font-family-primary, Pretendard)',
                  }}
                >
                  선생님이 롤링페이퍼를 열었습니다. 친구들의 메시지를 확인해 보세요.
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => navigate(`/class/public/birthday-rolling-paper/${activeBirthdayRollingPaperId}`)}
                >
                  열기
                </Button>
                <Button
                  variant="line"
                  size="medium"
                  onClick={() => navigate('/class/student/BirthdayRollingPaper')}
                >
                  상세
                </Button>
              </div>
            </div>
          </Card>
        </div>
      ) : null}

      
      {/* 대시보드 전체 영역*/}
      <div
        style={{
          width: '100%',
          minHeight: 0,
          gap: isMobile ? '12px' : '24px',
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          overflow: 'visible',
        }}
      >
        {/* 반, 일정, 캐릭터·포인트 영역 (이벤트 알림은 하단 카드) */}
        {!isMobile && (
        <div style={{
          width: '845px',
          minHeight: 0,
          gap: '24px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* 첫 번째 row: 반 정보, 일정 */}
          <div style={{
            width: '100%',
            height: '309px',
            gap: '24px',
            display: 'flex',
            flexDirection: 'row',
          }}>
            {/* 반 정보 카드 - 데스크톱만 표시 */}
            <Card 
              width="193px"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={10}
              style={{ display: 'flex', overflow: 'hidden', minWidth: 0 }}
            >
              <div style={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                flexDirection: 'column',
                justifyContent: 'flex-start', 
                alignItems: 'flex-start', 
                gap: 12, 
                display: 'flex'
              }}>
                {/* 담임 학급, 날짜 정보 영역 */}
                <div style={{
                  width: '100%',
                  flexShrink: 0,
                  alignSelf: 'stretch', 
                  flexDirection: 'column', 
                  justifyContent: 'flex-start', 
                  alignItems: 'flex-start', 
                  gap: 12, 
                  display: 'flex'
                }}>
                  <div style={{
                    width: '100%',
                    alignSelf: 'stretch', 
                    flexDirection: 'column', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    gap: 4, 
                    display: 'flex'
                  }}>
                    <div style={{
                      alignSelf: 'stretch', 
                      color: ' #161616', 
                      fontSize: '36px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '500', 
                      lineHeight: '54px', 
                      wordWrap: 'break-word'
                    }}>
                      {classInfo?.class ? `${classInfo.class}반` : '-'}
                    </div>
                    <div style={{
                      alignSelf: 'stretch', 
                      color: 'var(--Color-text-assistive, #989898)', 
                      fontSize: '14px', 
                      fontFamily: 'Pretendard', 
                      fontWeight: '400', 
                      lineHeight: '21px', 
                      wordWrap: 'break-word',
                      margin: 0
                    }}>
                      {schoolTermLabel()}
                    </div>
                  </div>
                  <div style={{
                    alignSelf: 'stretch', 
                    flexDirection: 'column', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    gap: 4, 
                    display: 'flex'
                  }}>
                    <div style={{
                      alignSelf: 'stretch', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      display: 'inline-flex'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)', 
                        fontSize: '14px', 
                        fontFamily: 'Pretendard', 
                        fontWeight: '500', 
                        lineHeight: '21px', 
                        wordWrap: 'break-word'
                      }}>
                        학년
                      </div>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)', 
                        fontSize: '14px', 
                        fontFamily: 'Pretendard',
                        fontWeight: '500', 
                        lineHeight: '21px', 
                        wordWrap: 'break-word',
                        margin: 0
                      }}>
                        {classInfo?.grade ? `${classInfo.grade}학년` : '-'}
                      </div>
                    </div>
                    <div style={{
                      alignSelf: 'stretch', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      display: 'inline-flex'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)', 
                        fontSize: '14px', 
                        fontFamily: 'Pretendard', 
                        fontWeight: '500', 
                        lineHeight: '21px', 
                        wordWrap: 'break-word',
                        margin: 0
                      }}>
                        학급 인원
                      </div>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)', 
                        fontSize: '14px', 
                        fontFamily: 'Pretendard', 
                        fontWeight: '500', 
                        lineHeight: '21px', 
                        wordWrap: 'break-word',
                        margin: 0
                      }}>
                        {`${studentCount}명`}
                      </div>
                    </div>
                    <div style={{
                      alignSelf: 'stretch', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      display: 'inline-flex'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)', 
                        fontSize: '14px', 
                        fontFamily: 'Pretendard', 
                        fontWeight: '500', 
                        lineHeight: '21px', 
                        wordWrap: 'break-word',
                        margin: 0
                      }}>
                        담임
                      </div>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)', 
                        fontSize: '14px', 
                        fontFamily: 'Pretendard', 
                        fontWeight: '500', 
                        lineHeight: '21px', 
                        wordWrap: 'break-word',
                        margin: 0
                      }}>
                        {classInfo?.teacherName ? `${classInfo.teacherName} 선생님` : '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 사진 전체 영역 */}
                <div style={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  flexDirection: 'column', 
                  justifyContent: 'flex-start', 
                  alignItems: 'center', 
                  gap: '8px', 
                  display: 'flex'
                }}>
                  {/* 사진 영역 */}
                  <div 
                    style={{
                      width: '100%',
                      flex: 1,
                      minHeight: 0,
                      background: '#D9D9D9', 
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <img 
                      style={{
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRadius: '12px'
                      }} 
                      src={classPhotos[currentPhotoIndex] || '/images/class/ClassSampleImage.png'}
                      alt="학급 사진"
                    />
                  </div>
                  {/* 슬라이더 인디케이터 */}
                  <div style={{
                    justifyContent: 'flex-start', 
                    alignItems: 'center', 
                    gap: '3px', 
                    display: 'inline-flex'
                  }}>
                    {[0, 1, 2, 3].map((index) => (
                      <div 
                        key={index}
                        onClick={() => {
                          if (index < classPhotos.length) {
                            goToPhoto(index);
                          }
                        }}
                        style={{
                          width: '3px', 
                          height: '3px', 
                          background: index === currentPhotoIndex && index < classPhotos.length 
                            ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                            : index < classPhotos.length 
                              ? '#989898'
                              : '#D9D9D9', 
                          borderRadius: 9999,
                          cursor: index < classPhotos.length ? 'pointer' : 'default',
                          transition: 'background 0.2s'
                        }} 
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* 일정 정보 카드 */}
            <Card 
              width="628px"
              height="309px"
              padding="16px 20px"
              flexDirection="row"
              alignItems="flex-start"
              gap="20px"
              style={{ overflow: 'hidden', minWidth: 0 }}
            >
              <div style={{
                flex: '0 0 220px',
                width: '220px',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                justifyContent: 'flex-start', 
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{
                  width: '100%', 
                  height: '50px',
                  flexShrink: 0,
                  display: 'flex', 
                  justifyContent: 'flex-start', 
                  alignItems: 'flex-start', 
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <div style={{
                    alignSelf: 'stretch',
                    color: 'var(--Color-text-normal, #161616)',
                    fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                    fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    lineHeight: '150%',
                    letterSpacing: '-0.18px',
                    margin: 0
                  }}>
                    {new Date().getFullYear()}년 {new Date().getMonth() + 1}월
                  </div>  
                  <div style={{
                    alignSelf: 'stretch',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                    fontSize: 'var(--Font-Font-size-Body-3, 14px)',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: '150%',
                    margin: 0
                  }}>
                    {new Date().getFullYear()}년 {new Date().getMonth() + 1 >= 3 && new Date().getMonth() + 1 <= 7 ? '1학기' : '2학기'}
                  </div>
                </div>
                {/* 일정 내용 영역 */}
                <div style={{ 
                  display: 'flex',
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  padding: '16px',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '12px',
                  alignSelf: 'stretch',
                  borderRadius: '12px',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: '100%',
                    height: '43px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '4px'
                  }}>
                    <div style={{
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '7px',
                      height: '21px'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        padding: '1.6px 2.27px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <img 
                          src={'/images/icons/calendar.svg'} 
                          alt="달력 아이콘"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: '150%',
                        margin: 0
                      }}>
                        {new Date().getDate()}일 {['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]}요일 일정
                      </span>
                    </div>
                    <div style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                      fontSize: '12px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '18px',
                      wordWrap: 'break-word'
                    }}>
                      오늘, 내 일정이 {schedules.filter(s => isScheduleOnDate(s, new Date().getDate(), new Date().getFullYear(), new Date().getMonth())).length}개 있습니다.
                    </div>
                  </div>
                  <div style={{ 
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '4px', 
                    overflow: 'auto'
                  }}>
                    {(() => {
                      const today = new Date();
                      const todaySchedules = schedules.filter(schedule => {
                        return isScheduleOnDate(schedule, today.getDate(), today.getFullYear(), today.getMonth());
                      });
                      
                      if (todaySchedules.length > 0) {
                        return todaySchedules.map((schedule) => (
                          <div 
                            key={schedule.id}
                            style={{ 
                              width: '100%',
                              minHeight: '42px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              alignItems: 'flex-start',
                              gap: '4px',
                              padding: '8px 0px',
                              borderBottom: '1px solid var(--Color-line-alternative, #E9E9E9)'
                            }}
                          >
                            <div style={{
                              width: '100%',
                              height: '20px',
                              display: 'flex', 
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              gap: '9px'
                            }}>
                              <div style={{
                                width: '4px',
                                height: '4px',
                                background: 'var(--Color-fill-brand-color, #FF6F0F)',
                                borderRadius: '9999'
                              }} />
                              <span style={{
                                color: 'var(--Color-text-normal, #161616)',
                                fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                fontWeight: '600',
                                lineHeight: '150%',
                                margin: 0
                              }}>
                                {schedule.title}
                              </span>
                            </div>
                            {schedule.description && (
                              <p style={{
                                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                                fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                                fontSize: 'var(--Font-Font-size-Label-2, 12px)',
                                fontStyle: 'normal',
                                fontWeight: '500',
                                lineHeight: '150%',
                                margin: 0
                              }}>
                                {schedule.description}
                              </p>
                            )}
                          </div>
                        ));
                      } else {
                        return (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--Color-text-assistive, #989898)',
                            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                            fontSize: '12px'
                          }}>
                            오늘 일정이 없습니다.
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
              {/* 달력 */}
              <div style={{ 
                flex: 1,
                minWidth: 0,
                height: '100%',
                minHeight: 0,
                display: 'flex', 
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                flexDirection: 'column',
                padding: '4px 0 0 0',
                gap: '8px',
                overflow: 'hidden',
              }}>
                <div style={{ 
                  width: '100%',
                  height: '20px',
                  flexShrink: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  columnGap: '4px',
                  alignItems: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%',
                }}>
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>일</span>
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>월</span>
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>화</span>
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>수</span>
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>목</span>
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>금</span>
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>토</span>
                </div>
                {/* 달력 그리드 */}
                <div style={{ 
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gridTemplateRows: 'repeat(6, minmax(0, 1fr))',
                  columnGap: '4px',
                  rowGap: '2px'
                }}>
                  {generateCalendar().map((day, index) => (
                    <div 
                      key={index}
                      style={{ 
                        display: 'flex',
                        width: '100%',
                        minHeight: 0,
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '2px',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        height: '24px',
                        padding: '3px',
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px', 
                        flexShrink: '0',
                        aspectRatio: '1/1',   
                        color: day.isCurrentMonth 
                          ? (day.isToday ? 'var(--Color-text-inverse, #FFF)' : 'var(--Color-text-normal, #161616)')
                          : 'var(--Color-text-assistive, #989898)',
                        backgroundColor: day.isToday ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                        borderRadius: day.isToday ? '50%' : '0',
                        textAlign: 'center',
                        fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                        fontSize: 'var(--Font-Font-size-Body-4, 13px)',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '150%'
                      }}>
                        {day.date}
                      </div>
                      <div style={{
                        display: 'flex',
                        width: '100%',
                        height: '8px',
                        flexShrink: 0,
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {day.isCurrentMonth && schedules.some(schedule => {
                          const calendarYear = new Date().getFullYear();
                          const calendarMonth = new Date().getMonth();
                          return isScheduleOnDate(schedule, day.date, calendarYear, calendarMonth);
                        }) && (
                          <img src={'/images/icons/circleSky.svg'} alt="일정 있음"/>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
          
          {/* 두 번째 row: 내 포인트 현황, 내 캐릭터 — 이벤트 알림은 하단 카드로 이동 */}
          <div style={{
            width: '100%',
            height: '309px',
            gap: '24px',
            display: 'flex',
            flexDirection: 'row',
          }}>
            {/* 포인트 현황 */}
            <Card
              width="372px"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={8}
              style={{ display: 'inline-flex' }}
            >
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
              }}>
                <div style={{
                  width: '100%',
                  height: '27px',
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, pretendard)',
                  fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '150%',
                  letterSpacing: '-0.18px',
                  margin: 0
                }}>
                  내 포인트 현황
                </div>
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  <div style={{
                    width: '100%',
                    height: '145px',
                    padding: '16px',
                    background: 'var(--Color-background-sub, #F8F8F8)',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '100%',
                      padding: '8px 20px',
                      background: 'var(--Color-background-normal, white)',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.50px'
                      }}>
                        내 역할
                      </div>
                      <div style={{
                        color: 'var(--Color-text-brand-color, #FF6F0F)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.50px'
                      }}>
                        {myRole?.name || '역할 없음'}
                      </div>
                    </div>
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '3px'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.50px'
                      }}>
                        {student.name}님의 보유 포인트
                      </div>
                      <div style={{
                        textAlign: 'center',
                        color: 'var(--Color-text-brand-color, #FF6F0F)',
                        fontSize: '28px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '42px'
                      }}>
                        {student.points?.toLocaleString() || 0} P
                      </div>
                    </div>
                  </div>
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      flex: '1 1 0',
                      minWidth: 0,
                      borderRight: '1px solid var(--Color-line-alternative, #E9E9E9)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      paddingRight: '16px'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.50px'
                      }}>
                        현재 나의 순위
                      </div>
                      <div style={{
                        textAlign: 'center',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '24px'
                      }}>
                        {myRank}위
                      </div>
                    </div>
                    <div style={{
                      flex: '1 1 0',
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      paddingLeft: '16px'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.50px'
                      }}>
                        💰 월급
                      </div>
                      <div style={{
                        textAlign: 'center',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '24px'
                      }}>
                        {monthlySalary} P
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            {/* 내 캐릭터 — 포인트·이벤트 카드와 동일한 제목/여백/본문 패턴 */}
            <Card
              width="449px"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={8}
              style={{ display: 'flex', overflow: 'hidden', minWidth: 0 }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '27px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontFamily: 'var(--Font-Typeface-Type, pretendard)',
                      fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '150%',
                      letterSpacing: '-0.18px',
                      margin: 0,
                    }}
                  >
                    내 캐릭터
                  </span>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: 'Pretendard',
                      color: 'var(--Color-fill-brand-color, #FF6F0F)',
                      lineHeight: '21px',
                      flexShrink: 0,
                    }}
                  >
                    Lv.{characterLevel}
                  </span>
                </div>
                <div
                  style={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      flex: 1,
                      minHeight: 0,
                      padding: '16px',
                      background: 'var(--Color-background-sub, #F8F8F8)',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '16px',
                    }}
                  >
                    <StudentCharacterPortrait
                      src={characterImage}
                      overlays={equippedLayerImages}
                      width={96}
                      height={148}
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          width: '100%',
                        }}
                        aria-label="캐릭터 능력치"
                      >
                        {CHARACTER_STAT_KEYS.map((key) => {
                          const v = characterStats[key];
                          const pct = (v / 10) * 100;
                          return (
                            <div
                              key={key}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                              }}
                            >
                              <span
                                style={{
                                  width: '36px',
                                  flexShrink: 0,
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  color: 'var(--Color-text-assistive, #989898)',
                                  fontWeight: 500,
                                }}
                              >
                                {CHARACTER_STAT_LABELS[key]}
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  height: '6px',
                                  borderRadius: '4px',
                                  background: 'var(--Color-line-normal, #F0F0F0)',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    width: `${pct}%`,
                                    height: '100%',
                                    background: 'var(--Color-fill-brand-color, #FF6F0F)',
                                    borderRadius: '4px',
                                  }}
                                />
                              </div>
                              <span
                                style={{
                                  width: '20px',
                                  flexShrink: 0,
                                  textAlign: 'right',
                                  fontSize: '12px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: 600,
                                  color: 'var(--Color-text-normal, #161616)',
                                }}
                              >
                                {v}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {FEATURE_CHARACTER_COSMETIC && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => navigate('/class/student/ClassItemShop')}
                            style={{
                              height: '36px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: '1px solid var(--Color-line-normal, #F0F0F0)',
                              background: 'white',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 500,
                            }}
                          >
                            코스메틱 상점
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate('/class/student/CharacterWardrobe')}
                            style={{
                              height: '36px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'var(--Color-fill-normal, #293044)',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 500,
                            }}
                          >
                            옷장 열기
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
        )}

        {/* 반 정보 카드 - 모바일에서만 표시 */}
        {isMobile && (
          <Card 
            width="100%"
            height="100%"
            padding={isMobile ? "16px 20px" : "16px 20px"}
            alignItems="flex-start"
            gap={isMobile ? 12 : 10}
            style={{ 
              display: 'inline-flex',
              minHeight: isMobile ? 0 : "100%",
              flex: isMobile ? 1 : undefined,
              overflow: isMobile ? 'hidden' : undefined,
            }} 
          >
            <div style={{
              width: '100%',
              height: '100%',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: isMobile ? 24 : 34,
              display: 'flex',
              flex: 1,
              minHeight: 0,
            }}>
              {/* 담임 학급, 날짜 정보 영역 */}
              <div style={{
                width: '100%',
                height: isMobile ? 'auto' : '141px',
                minHeight: isMobile ? 'auto' : '141px',
                alignSelf: 'stretch',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: isMobile ? 16 : 16,
                display: 'flex',
                flexShrink: 0,
              }}>
                <div style={{
                  width: isMobile ? '100%' : '153px',
                  height: '100%',
                  alignSelf: 'stretch',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: 4,
                  display: 'flex'
                }}>
                  {isMobile ? (
                    // 모바일: 학년과 반을 합쳐서 표시
                    <div style={{
                      alignSelf: 'stretch',
                      color: ' #161616',
                      fontSize: '32px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '48px',
                      wordWrap: 'break-word'
                    }}>
                      {classInfo?.grade && classInfo?.class 
                        ? `${classInfo.grade}학년 ${classInfo.class}반` 
                        : classInfo?.class 
                          ? `${classInfo.class}반` 
                          : '-'}
                    </div>
                  ) : (
                    // 데스크톱: 기존 형식 유지
                    <>
                      <div style={{
                        alignSelf: 'stretch',
                        color: ' #161616',
                        fontSize: '36px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '54px',
                        wordWrap: 'break-word'
                      }}>
                        {classInfo?.class ? `${classInfo.class}반` : '-'}
                      </div>
                      <div style={{
                        alignSelf: 'stretch',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: '21px',
                        wordWrap: 'break-word',
                        margin: 0
                      }}>
                        {schoolTermLabel()}
                      </div>
                    </>
                  )}
                </div>
                <div style={{
                  alignSelf: 'stretch',
                  flexDirection: isMobile ? 'column' : 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: isMobile ? 8 : 4,
                  display: 'flex'
                }}>
                  {isMobile ? (
                    // 모바일: 기간과 인원을 한 줄에 배치
                    <div style={{
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: '24px',
                        wordWrap: 'break-word',
                        margin: 0
                      }}>
                        {schoolTermLabel()}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '16px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '24px',
                          wordWrap: 'break-word',
                          margin: 0
                        }}>
                          학급 인원
                        </div>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '16px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '24px',
                          wordWrap: 'break-word',
                          margin: 0
                        }}>
                          {`${studentCount}명`}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // 데스크톱: 기존 형식 유지
                    <>
                      <div style={{
                        alignSelf: 'stretch',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        display: 'inline-flex'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '21px',
                          wordWrap: 'break-word'
                        }}>
                          학년
                        </div>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '21px',
                          wordWrap: 'break-word',
                          margin: 0
                        }}>
                          {classInfo?.grade ? `${classInfo.grade}학년` : '-'}
                        </div>
                      </div>
                      <div style={{
                        alignSelf: 'stretch',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        display: 'inline-flex'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '21px',
                          wordWrap: 'break-word',
                          margin: 0
                        }}>
                          학급 인원
                        </div>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '21px',
                          wordWrap: 'break-word',
                          margin: 0
                        }}>
                          {`${studentCount}명`}
                        </div>
                      </div>
                    </>
                  )}
                  <div style={{
                    alignSelf: 'stretch',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'inline-flex'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: isMobile ? '16px' : '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: isMobile ? '24px' : '21px',
                      wordWrap: 'break-word',
                      margin: 0
                    }}>
                      담임
                    </div>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: isMobile ? '16px' : '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: isMobile ? '24px' : '21px',
                      wordWrap: 'break-word',
                      margin: 0
                    }}>
                      {classInfo?.teacherName ? `${classInfo.teacherName} 선생님` : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* 사진 전체 영역 */}
              <div style={{
                width: '100%',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
                display: 'flex',
                flex: isMobile ? 1 : undefined,
                minHeight: 0,
              }}>
                {/* 사진 영역 */}
                <div
                  style={{
                    width: '100%',
                    height: isMobile ? '200px' : '91px',
                    minHeight: isMobile ? '200px' : '91px',
                    background: '#D9D9D9',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <img
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      transition: 'opacity 0.2s'
                    }}
                    src={classPhotos[currentPhotoIndex] || '/images/class/ClassSampleImage.png'}
                    alt="학급 사진"
                  />
                </div>
                {/* 슬라이더 인디케이터 */}
                <div style={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '3px',
                  display: 'inline-flex'
                }}>
                  {[0, 1, 2, 3].map((index) => (
                    <div
                      key={index}
                      onClick={() => {
                        if (index < classPhotos.length) {
                          goToPhoto(index);
                        }
                      }}
                      style={{
                        width: '3px',
                        height: '3px',
                        background: index === currentPhotoIndex && index < classPhotos.length
                          ? 'var(--Color-fill-brand-color, #FF6F0F)'
                          : index < classPhotos.length
                            ? '#989898'
                            : '#D9D9D9',
                        borderRadius: 9999,
                        cursor: index < classPhotos.length ? 'pointer' : 'default',
                        transition: 'background 0.2s'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* 시간표 + 오늘 급식 */}
        <div
          style={{
            width: isMobile ? '100%' : '411px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
        {/* 시간표 - 데스크톱만 표시 */}
        {!isMobile && (
        <Card 
          width="100%"
          height="642px" 
          padding="16px 20px" 
          alignItems="flex-start" 
          gap={24} 
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: '642px',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ 
            width: '100%',
            height: '50px',
            display: 'inline-flex',
            flexDirection: 'row',
            gap: '8px'
          }}>
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '18px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '27px',
                margin: 0
              }}>
                시간표
              </div>
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '400',
                lineHeight: '21px',
                margin: 0
              }}>
                {timetableData.year}년 {timetableData.semester}
                {timetableSource === 'comcigan' ? ' · 컴시간 · 이번 주' : timetableSource === 'neis' ? ' · 이번 주' : ''}
              </div>
            </div>
          </div>
          <div style={{
            width: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flex: 1,
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: '4px',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '18px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '27px'
            }}>
              {(neisTimetableLoading || comciganTimetableLoading) && timetableSource === 'empty' ? (
                <>학교 시간표를 불러오는 중입니다</>
              ) : timetableSource === 'empty' ? (
                <>아직 표시할 시간표가 없습니다</>
              ) : currentTimetableStatus?.kind === 'period' ? (
                <>
                  지금은{' '}
                  <span style={{ color: 'var(--Color-text-brand-color, #FF6F0F)' }}>
                    {currentTimetableStatus.period}교시 {currentTimetableStatus.subject}
                  </span>
                  {' '}시간입니다
                </>
              ) : currentTimetableStatus?.kind === 'lunch' ? (
                <>
                  지금은{' '}
                  <span style={{ color: 'var(--Color-text-brand-color, #FF6F0F)' }}>
                    점심시간
                  </span>
                  입니다
                </>
              ) : (
                <>지금은 수업 시간이 아닙니다</>
              )}
            </div>
            <div style={{ 
              width: '100%',
              minHeight: 0,
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              flex: 1,
              overflow: 'hidden'
            }}>
              <div style={{
                width: '100%',
                height: '28px',
                display: 'flex',
                flexDirection: 'row',
                gap: '50px',
                fontSize: '13px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '19.50px',
                padding: '4px 29px 4px 82px', 
                background: 'var(--Color-background-sub, #F8F8F8)', 
                borderRadius: '8px', 
                justifyContent: 'center', 
                alignItems: 'center'
              }}>
                <div style={{
                  color: new Date().getDay() === 1 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  월
                </div>
                <div style={{
                  color: new Date().getDay() === 2 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  화
                </div>
                <div style={{
                  color: new Date().getDay() === 3 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  수
                </div>
                <div style={{
                  color: new Date().getDay() === 4 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  목
                </div>
                <div style={{
                  color: new Date().getDay() === 5 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)'
                }}>
                  금
                </div>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {timetableData.entries.slice(0, 4).map((entry) => (
                  <div key={entry.period} style={{
                    height: '48px',
                    gap: '18px',
                    padding: '0px 8px 10px 8px',
                    display: 'inline-flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontFamily: 'Pretendard',
                    wordWrap: 'break-word',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '13px',
                        lineHeight: '19.50px',
                        display: 'flex',
                        color: 'var(--Color-text-normal, #161616)' 
                      }}>
                        {entry.period}교시
                      </div>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '12px',
                        lineHeight: '18px',
                        width: '33px',
                        flexShrink: '0',
                        display: 'flex',
                        color: 'var(--Color-text-assistive, #989898)' 
                      }}>
                        {entry.time}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      width: '305px',
                      height: '20px',
                      padding: '0px 4px',
                      gap: '13px',
                      fontSize: '13px',
                      flexDirection: 'row',
                      lineHeight: '19.50px',
                      wordWrap: 'break-word',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      flexShrink: '0'
                    }}>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 1 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.monday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 2 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.tuesday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 3 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.wednesday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 4 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.thursday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 5 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.friday || '-'}
                      </span>
                    </div>
                  </div>
                ))}
                
                <div style={{
                  height: '48px',
                  gap: '18px',
                  padding: '0px 8px 10px 8px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  wordWrap: 'break-word',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  fontSize: '13px',
                  lineHeight: '19.50px',
                  color: 'var(--Color-text-normal, #161616)'
                }}>
                  😋 점심시간
                </div>

                {timetableData.entries.slice(4, 7).map((entry) => (
                  <div key={entry.period} style={{
                    height: '48px',
                    gap: '18px',
                    padding: '0px 8px 10px 8px',
                    display: 'inline-flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontFamily: 'Pretendard',
                    wordWrap: 'break-word',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '13px',
                        lineHeight: '19.50px',
                        display: 'flex',
                        color: 'var(--Color-text-normal, #161616)' 
                      }}>
                        {entry.period}교시
                      </div>
                      <div style={{
                        justifyContent: 'center',
                        fontSize: '12px',
                        lineHeight: '18px',
                        width: '33px',
                        flexShrink: '0',
                        display: 'flex',
                        color: 'var(--Color-text-assistive, #989898)' 
                      }}>
                        {entry.time}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      width: '305px',
                      height: '20px',
                      padding: '0px 4px',
                      gap: '13px',
                      fontSize: '13px',
                      flexDirection: 'row',
                      lineHeight: '19.50px',
                      wordWrap: 'break-word',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      flexShrink: '0'
                    }}>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 1 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.monday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 2 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.tuesday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 3 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.wednesday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 4 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.thursday || '-'}
                      </span>
                      <span style={{ 
                        width: '49px',
                        color: entry.period === currentPeriod?.period && new Date().getDay() === 5 ? 'var(--Color-text-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                      }}>
                        {entry.friday || '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
        )}
        <TodayMealCard
          officeCode={schoolInfo?.neisOfficeCode}
          schoolCode={schoolInfo?.neisSchoolCode}
          width="100%"
        />
        </div>
      </div>

      {/* 데스크톱: 이벤트 알림 + 학급 포인트(캐릭터는 대시보드 2행). 모바일: 캐릭터+포인트 카드 후 이벤트 알림 카드 */}
      {currentUser?.schoolId && currentUser?.classId && (
        <>
          {isMobile ? (
            <>
              <Card
                width="100%"
                padding="12px 16px"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'stretch',
                  gap: 12,
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  marginTop: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 42,
                      height: 72,
                      borderRadius: 10,
                      background: 'var(--Color-background-sub, #F8F8F8)',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    <StudentCharacterPortrait
                      src={characterImage}
                      overlays={equippedLayerImages}
                      width={42}
                      height={72}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                        fontSize: 15,
                        fontWeight: 600,
                      }}
                    >
                      내 캐릭터
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                        color: 'var(--Color-fill-brand-color, #FF6F0F)',
                      }}
                    >
                      Lv.{characterLevel}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                    color: 'var(--Color-text-normal, #161616)',
                    fontWeight: 500,
                    lineHeight: '16px',
                  }}
                  aria-label="캐릭터 능력치"
                >
                  {CHARACTER_STAT_KEYS.map((key, i) => (
                    <span key={key}>
                      {i > 0 ? ' · ' : ''}
                      {CHARACTER_STAT_LABELS[key]} {characterStats[key]}
                    </span>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 20,
                    borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
                    paddingTop: 12,
                  }}
                >
                  <span
                    style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    학급 전체 포인트
                  </span>
                  <span
                    style={{
                      color: 'var(--Color-text-strong, #0D0D0D)',
                      fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                      fontSize: 24,
                      fontWeight: 600,
                    }}
                  >
                    {classPoints.toLocaleString()}pt
                  </span>
                </div>
              </Card>
              <Card
                width="100%"
                padding="12px 16px"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  marginTop: 12,
                  maxHeight: 'min(280px, 42vh)',
                  overflow: 'hidden',
                }}
              >
                {eventNotificationsBlock}
              </Card>
            </>
          ) : (
            <Card
              width="100%"
              padding="16px 20px"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'stretch',
                gap: 16,
                boxSizing: 'border-box',
                flexShrink: 0,
                marginTop: 20,
                minHeight: '200px',
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  paddingRight: 16,
                  borderRight: '1px solid var(--Color-line-normal, #F0F0F0)',
                  overflow: 'hidden',
                }}
              >
                {eventNotificationsBlock}
              </div>

              <div
                style={{
                  display: 'flex',
                  flex: '0 0 auto',
                  width: '220px',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  paddingLeft: 16,
                }}
              >
                <span
                  style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  학급 전체 포인트
                </span>
                <span
                  style={{
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
                    fontSize: 24,
                    fontWeight: 600,
                  }}
                >
                  {classPoints.toLocaleString()}pt
                </span>
              </div>
            </Card>
          )}
        </>
      )}

    </div>
  );
};

export default ClassStudentHome;
